import jsPDF from 'jspdf';
import DatabaseService from './database';
import { getImageDimensions, ensureDataUrl } from '../utils/imageUtils';
import { renderForPdf as renderBackgroundForPdf } from '../utils/pedigreeBackground';

// Turn any text into a dash-joined, filesystem-safe slug: runs of anything
// that isn't a letter or digit collapse to a single dash ("GB 1992" -> "GB-1992",
// "NL/04" -> "NL-04"), with no leading/trailing dashes. Unicode letters are
// kept ("Müller" -> "Müller", not "M-ller") — filesystems handle them fine.
const slugify = (value) =>
  String(value == null ? '' : value)
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');

class TraditionalPdfGenerator {
  constructor() {
    // Page dimensions (A4 portrait)
    this.PAGE_WIDTH = 210;
    this.PAGE_HEIGHT = 297;
    // 5mm is about the safe floor for consumer printers; below that, edges
    // risk being clipped when the pedigree is printed.
    this.MARGIN = 5;
    this.CONTENT_WIDTH = this.PAGE_WIDTH - (this.MARGIN * 2);
    this.CONTENT_HEIGHT = this.PAGE_HEIGHT - (this.MARGIN * 2);
    
    // Layout dimensions
    this.HEADER_HEIGHT = 0;  // no dedicated header box; we use a combined top panel
    this.MAIN_BIRD_HEIGHT = 50;  // Reduced from 80
    this.BIRD_BOX_HEIGHT = 30;  // Reduced from 35
    this.BIRD_BOX_WIDTH = 85;
    this.PHOTO_SIZE = 20;  // Reduced from 25
    // Top panel: details column fraction and inner padding (also used by tests)
    this.TOP_DETAILS_FRAC = 0.35;
    this.PANEL_PAD = 3;
    // Subject-pigeon photos live in the top panel. Keep these separate from
    // the sire/dam photo dimensions in drawParentSection so changing the
    // featured bird never changes its parents' layout.
    this.SUBJECT_PHOTO_PANEL_HEIGHT = 44;
    this.SUBJECT_PHOTO_MAX_WIDTH = 36;
    // When the featured bird has only ONE photo (usually just a body shot),
    // it gets a larger box instead of the smaller side-by-side width.
    this.SUBJECT_PHOTO_MAX_WIDTH_SINGLE = 52;
    
    // Colors (RGB arrays for jsPDF)
    this.COLORS = {
      black: [0, 0, 0],
      darkGray: [64, 64, 64],
      gray: [128, 128, 128],
      lightGray: [192, 192, 192],
      white: [255, 255, 255],
      blue: [0, 100, 200],
      lightBlue: [230, 240, 255]
    };
  }

  // Wrap each segment of a text stack to maxWidth using its own font, and
  // return the segments with their wrapped `lines`. This is the single
  // layout primitive shared by measureTextStack (height planning) and
  // drawTextStack (drawing), so measurement and drawing can never drift.
  splitStackSegments(doc, segments, maxWidth) {
    const segs = [];
    for (const seg of segments) {
      const text = String(seg.text == null ? '' : seg.text);
      if (!text) continue;
      doc.setFont('helvetica', seg.fontStyle || 'normal');
      doc.setFontSize(seg.size);
      segs.push({ ...seg, lines: doc.splitTextToSize(text, maxWidth) });
    }
    return segs;
  }

  // How many wrapped lines a stack of segments will occupy at maxWidth.
  measureTextStack(doc, segments, maxWidth) {
    return this.splitStackSegments(doc, segments, maxWidth)
      .reduce((n, seg) => n + seg.lines.length, 0);
  }

  // Shorten `text` so that it (plus "...") fits in maxWidth using the doc's
  // CURRENT font/size. Returns the text unchanged when it already fits;
  // pass forceEllipsis when the caller knows content after this text was
  // cut, so the "..." must appear even if the text itself fits.
  truncateToWidth(doc, text, maxWidth, forceEllipsis = false) {
    const str = String(text == null ? '' : text);
    if (!forceEllipsis && doc.getTextWidth(str) <= maxWidth) return str;
    const ellipsis = '...';
    let last = str;
    while (last.length > 0 && doc.getTextWidth(last + ellipsis) > maxWidth) {
      last = last.slice(0, -1);
    }
    return last.replace(/\s+$/, '') + ellipsis;
  }

  // Draw a stack of text segments — each with its own font style, size and
  // line height — wrapped to maxWidth, with baselines flowing down from `y`
  // but never past `maxY`. The stack is laid out fully before drawing, so
  // when part of it is cut off (even a whole trailing segment), the last
  // visible line gets "..." to show there was more. If `y` already starts
  // past `maxY` (no room for even one line), nothing is drawn at all.
  // Nothing ever spills into neighbouring content areas. Returns the
  // baseline for the next line.
  drawTextStack(doc, segments, x, y, maxWidth, maxY) {
    const segs = this.splitStackSegments(doc, segments, maxWidth);

    // Layout pass: clip to maxY and detect truncation before drawing.
    let cursor = y;
    let cut = false;
    for (const seg of segs) {
      seg.fit = [];
      for (const line of seg.lines) {
        if (cursor > maxY) break;
        seg.fit.push({ line, y: cursor });
        cursor += seg.lineH;
      }
      if (seg.fit.length < seg.lines.length) cut = true;
    }

    // Ellipsize the last visible line when anything below it was cut.
    if (cut) {
      for (let i = segs.length - 1; i >= 0; i--) {
        const fit = segs[i].fit;
        if (!fit.length) continue;
        doc.setFont('helvetica', segs[i].fontStyle || 'normal');
        doc.setFontSize(segs[i].size);
        fit[fit.length - 1].line = this.truncateToWidth(doc, fit[fit.length - 1].line, maxWidth, true);
        break;
      }
    }

    for (const seg of segs) {
      if (!seg.fit.length) continue;
      doc.setFont('helvetica', seg.fontStyle || 'normal');
      doc.setFontSize(seg.size);
      for (const l of seg.fit) doc.text(l.line, x, l.y);
    }

    return cursor;
  }

  // Draw a text stack down one column and continue in the next when the
  // first runs out of vertical room. Whole fields move together whenever
  // they fit in the next column; only a field taller than a complete column
  // is split. This keeps labels such as Color/Sex/Strain easy to scan.
  layoutTextStackColumns(doc, segments, columns) {
    const segs = this.splitStackSegments(doc, segments, columns[0]?.maxWidth || 0);
    const placed = [];
    let colIndex = 0;
    let cursor = columns[0]?.y || 0;
    let cut = false;

    const remainingLines = (col, lineH) => Math.max(
      0,
      Math.floor((col.maxY - cursor) / lineH) + 1
    );

    for (const seg of segs) {
      let col = columns[colIndex];
      if (!col) {
        cut = true;
        break;
      }

      // Prefer moving an intact field to the next column instead of leaving
      // its label on one side and its value/continuation on the other.
      if (
        seg.lines.length > remainingLines(col, seg.lineH) &&
        colIndex + 1 < columns.length
      ) {
        const next = columns[colIndex + 1];
        const nextCapacity = Math.max(0, Math.floor((next.maxY - next.y) / seg.lineH) + 1);
        if (seg.lines.length <= nextCapacity) {
          colIndex += 1;
          col = next;
          cursor = col.y;
        }
      }

      for (const line of seg.lines) {
        col = columns[colIndex];
        while (col && cursor > col.maxY && colIndex + 1 < columns.length) {
          colIndex += 1;
          col = columns[colIndex];
          cursor = col.y;
        }
        if (!col || cursor > col.maxY) {
          cut = true;
          break;
        }
        placed.push({
          line,
          x: col.x,
          y: cursor,
          maxWidth: col.maxWidth,
          fontStyle: seg.fontStyle,
          size: seg.size
        });
        cursor += seg.lineH;
      }
      if (cut) break;
    }

    if (cut && placed.length) {
      const last = placed[placed.length - 1];
      doc.setFont('helvetica', last.fontStyle || 'normal');
      doc.setFontSize(last.size);
      last.line = this.truncateToWidth(doc, last.line, last.maxWidth, true);
    }

    return { lines: placed, truncated: cut, usedColumns: placed.length ? colIndex + 1 : 0 };
  }

  drawTextStackColumns(doc, segments, columns) {
    const layout = this.layoutTextStackColumns(doc, segments, columns);
    for (const line of layout.lines) {
      doc.setFont('helvetica', line.fontStyle || 'normal');
      doc.setFontSize(line.size);
      doc.text(line.line, line.x, line.y);
    }

    return { truncated: layout.truncated, usedColumns: layout.usedColumns };
  }

  // Fit an image into a box preserving aspect ratio; returns draw rect.
  fitImage(imgW, imgH, boxX, boxY, boxW, boxH) {
    const scale = Math.min(boxW / imgW, boxH / imgH);
    const w = imgW * scale;
    const h = imgH * scale;
    return { x: boxX + (boxW - w) / 2, y: boxY + (boxH - h) / 2, w, h };
  }

  // Draw a photo inside a bordered box, preserving aspect ratio. Desktop
  // passes data URLs; web builds pass /api/photos/ URLs which are fetched
  // and inlined first (same-origin, so the session cookie rides along).
  async drawPhoto(doc, photoSrc, boxX, boxY, boxW, boxH) {
    try {
      const dataUrl = await ensureDataUrl(photoSrc);
      const formatMatch = /^data:image\/(png|jpe?g)/i.exec(dataUrl || '');
      if (!formatMatch) return false;
      const format = formatMatch[1].toLowerCase() === 'png' ? 'PNG' : 'JPEG';
      const { width, height } = await getImageDimensions(dataUrl);
      const rect = this.fitImage(width, height, boxX, boxY, boxW, boxH);
      doc.addImage(dataUrl, format, rect.x, rect.y, rect.w, rect.h);
      doc.setDrawColor(...this.COLORS.gray);
      doc.setLineWidth(0.2);
      doc.rect(rect.x, rect.y, rect.w, rect.h);
      return true;
    } catch (error) {
      console.error('Could not embed photo in PDF:', error);
      return false;
    }
  }

  // Full-bleed background image, drawn before anything else so every panel,
  // box and line lands on top of it. The image arrives already washed out
  // (see utils/pedigreeBackground) — the wash is baked into the JPEG rather
  // than expressed as PDF transparency, so no viewer or printer can render it
  // darker than intended and hide the text. A background that fails to load
  // is skipped silently: a missing decoration must never cost the breeder
  // their pedigree.
  async drawBackground(doc, settings) {
    if (!settings?.pedigreeBackground) return false;
    try {
      const image = await renderBackgroundForPdf(
        settings.pedigreeBackground,
        settings.pedigreeBackgroundFade
      );
      if (!image) return false;
      doc.addImage(image, 'JPEG', 0, 0, this.PAGE_WIDTH, this.PAGE_HEIGHT);
      return true;
    } catch (error) {
      console.error('Could not draw pedigree background:', error);
      return false;
    }
  }

  // Combined top panel with loft branding, compact subject details, and larger notes
  async drawTopPanel(doc, bird, settings) {
    const x = this.MARGIN;
    const y = this.MARGIN;
    const w = this.CONTENT_WIDTH;
    const hasPhotos = Boolean(bird.body_photo || bird.eye_photo);
    // Details column is 35% of the panel width (also used for name wrapping).
    const detailsW = Math.floor(w * this.TOP_DETAILS_FRAC);
    const pad = this.PANEL_PAD;
    const lineH = 4;
    const detailTextW = detailsW - pad * 2;

    // Get year from date_of_birth or year field
    let year = null;
    if (bird.date_of_birth) {
      const d = new Date(bird.date_of_birth);
      if (!isNaN(d.getTime())) year = d.getFullYear();
    }
    if (year == null && typeof bird.year !== 'undefined') year = bird.year;

    // Format ring number with year if available (year in 2-digit format)
    const shortYear = year ? String(year).slice(-2) : '';
    const ring = bird.ring_number ? `${bird.ring_number}${shortYear ? ' ' + shortYear : ''}` : '';
    const color = bird.color ? `Color: ${bird.color}` : null;
    const sex = bird.sex ? `Sex: ${bird.sex === 'male' ? 'Cock' : bird.sex === 'female' ? 'Hen' : bird.sex}` : null;
    const breed = bird.breed ? `Strain: ${bird.breed}` : null;
    const details = [color, sex, breed].filter(Boolean);
    let contact = '';
    if (settings?.loftPhone) contact += `Tel: ${settings.loftPhone}`;
    if (settings?.loftEmail) contact += (contact ? ' | ' : '') + `eMail: ${settings.loftEmail}`;

    // The detail stack: ring, then name, then Color/Sex/Strain — each on its
    // own wrapped line so a long value can never run across the divider into
    // the notes. Built once here; measured for the height calculation and
    // then drawn below, so measurement and drawing can never disagree.
    const stack = [];
    if (ring) stack.push({ text: ring, fontStyle: 'bold', size: 8, lineH });
    if (bird.name) stack.push({ text: bird.name, fontStyle: 'bold', size: 9, lineH });
    for (const part of details) {
      stack.push({ text: part, fontStyle: 'normal', size: 8, lineH });
    }
    const stackLines = this.measureTextStack(doc, stack, detailTextW);

    // How far up the stack may move: the centered loft name (12pt, baseline
    // y+8) and slogan (8pt, baseline y+13) are drawn without a width limit,
    // so the stack may only rise into rows they don't actually reach.
    // Measure their real extent against the details column instead of
    // assuming they stay clear of it.
    const loftName = (settings?.loftName || '').toString();
    const loftSlogan = (settings?.loftSlogan || '').toString();
    const colRight = x + detailsW;
    let minTop = y + 10;
    if (loftName) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      if (x + (w - doc.getTextWidth(loftName.toUpperCase())) / 2 < colRight) {
        minTop = y + 13; // below the loft name's glyphs
      }
    }
    if (loftSlogan) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      if (x + (w - doc.getTextWidth(loftSlogan)) / 2 < colRight) {
        minTop = y + 17; // below the slogan's glyphs
      }
    }

    // Reserve branding rows consistently, even for short detail lists. Flow
    // into another column before growing the panel, then cap it at 60mm so
    // the pedigree grid keeps enough room.
    const baseH = hasPhotos ? this.SUBJECT_PHOTO_PANEL_HEIGHT : 31;
    const detailTop = Math.max(minTop, loftSlogan ? y + 15 : y + 10);
    const MAX_H = 60;
    const neededH = (detailTop - y) + Math.max(0, stackLines - 1) * lineH + pad + (contact ? lineH : 0);
    let h = Math.max(baseH, Math.min(MAX_H, neededH));

    const innerTop = y + 13;
    const photos = [bird.body_photo, bird.eye_photo].filter(Boolean);
    const notesText = (settings?.notes || bird?.notes || '').toString();

    // Reserve the same horizontal strip that the photos will occupy below.
    // A continuation column is only allowed when it fits before that strip
    // and still leaves a useful notes column whenever notes are present.
    let photoReserveW = 0;
    if (photos.length === 1) {
      photoReserveW = Math.min(this.SUBJECT_PHOTO_MAX_WIDTH_SINGLE, w - detailsW - pad * 3) + pad;
    } else if (photos.length > 1) {
      const basePhotoH = baseH - (innerTop - y) - pad;
      const basePhotoW = Math.min(this.SUBJECT_PHOTO_MAX_WIDTH, basePhotoH + 8);
      photoReserveW = Math.min(2, photos.length) * (basePhotoW + pad);
    }

    const secondColumnX = x + detailsW + pad;
    const contentRight = x + w - pad - photoReserveW;
    const minNotesW = notesText ? 30 : 0;
    const secondColumnFits =
      secondColumnX + detailTextW + (notesText ? pad + minNotesW : 0) <= contentRight;
    const contactReserve = contact ? lineH : 0;
    const capacityAt = (height, top, reserve = 0) => Math.max(
      0,
      Math.floor(((y + height - pad - reserve) - top) / lineH) + 1
    );
    const firstBaseCapacity = capacityAt(baseH, detailTop, contactReserve);
    // The second column is nearer the centered branding. Without a slogan it
    // can start one row higher; with no branding at all it starts at y + 10.
    const secondTop = loftSlogan ? y + 17 : (loftName ? y + 13 : y + 10);
    const useSecondColumn = stackLines > firstBaseCapacity && secondColumnFits;
    const detailColumnsAt = (height) => [
      { x: x + pad, y: detailTop, maxWidth: detailTextW, maxY: y + height - pad - contactReserve },
      { x: secondColumnX, y: secondTop, maxWidth: detailTextW, maxY: y + height - pad }
    ];

    if (useSecondColumn) {
      h = baseH;
      // Measure the actual field placement, including blank rows left when a
      // whole field moves columns. A raw line count can silently lose fields.
      while (
        h < MAX_H &&
        this.layoutTextStackColumns(doc, stack, detailColumnsAt(h)).truncated
      ) {
        h = Math.min(h + lineH, MAX_H);
      }
    }

    // Notes (including race results) have their own overflow flow. Reserve
    // photos first, then split the remaining notes area only when both text
    // columns have a readable width. Use the same layout for sizing/drawing.
    const activeDetailsW = detailsW * (useSecondColumn ? 2 : 1);
    const notesX = x + activeDetailsW + pad;
    const notesTop = loftSlogan ? innerTop + 5 : secondTop;
    const notesWidth = w - activeDetailsW - pad * 2 - photoReserveW;
    const notesStack = [{ text: notesText, fontStyle: 'normal', size: 7, lineH: 3 }];
    const notesColumnWidth = (notesWidth - pad) / 2;
    const notesOverflow = notesText && notesWidth > 0 &&
      this.measureTextStack(doc, notesStack, notesWidth) >
        Math.max(0, Math.floor((y + h - pad - notesTop) / 3) + 1);
    const useNotesColumns = Boolean(notesOverflow && notesColumnWidth >= 30);
    const notesColumnsAt = (height) => {
      const first = {
        x: notesX, y: notesTop, maxWidth: useNotesColumns ? notesColumnWidth : notesWidth,
        maxY: y + height - pad
      };
      return useNotesColumns
        ? [first, { ...first, x: notesX + notesColumnWidth + pad }]
        : [first];
    };
    if (notesText && notesWidth > 0) {
      while (h < MAX_H && this.layoutTextStackColumns(doc, notesStack, notesColumnsAt(h)).truncated) {
        h = Math.min(h + 3, MAX_H);
      }
    }

    // Panel border
    doc.setDrawColor(...this.COLORS.black);
    doc.setLineWidth(0.5);
    doc.rect(x, y, w, h);

    // Loft name centered on first line
    if (loftName) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...this.COLORS.black);
      doc.text(loftName.toUpperCase(), x + w / 2, y + pad + 5, { align: 'center' });
    }
    if (loftSlogan) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(...this.COLORS.black);
      doc.text(loftSlogan, x + w / 2, y + pad + 10, { align: 'center' });
    }

    // Move the divider when a continuation column is active so notes begin
    // after the complete details block.

    // Vertical divider centered in the gutter
    const dividerX = x + activeDetailsW + pad / 2;
    doc.setDrawColor(...this.COLORS.lightGray);
    doc.setLineWidth(0.3);
    doc.line(dividerX, innerTop, dividerX, y + h - pad);
    if (useSecondColumn) {
      const continuationDividerX = x + detailsW + pad / 2;
      doc.line(continuationDividerX, Math.max(detailTop, secondTop) - 2,
        continuationDividerX, y + h - pad);
    }
    if (useNotesColumns) {
      const notesDividerX = notesX + notesColumnWidth + pad / 2;
      doc.line(notesDividerX, notesTop - 2, notesDividerX, y + h - pad);
    }
    doc.setDrawColor(...this.COLORS.black);
    doc.setLineWidth(0.5);

    // Subject details (compact): the stack measured above. The contact line
    // is pinned to the panel bottom; the stack clamps one line above it so
    // nothing ever overlaps it.
    const tx = x + pad;
    const contactY = contact ? y + h - pad : null;
    const detailMaxY = (contact ? contactY - lineH : y + h - pad);
    doc.setTextColor(...this.COLORS.black);
    if (useSecondColumn) {
      this.drawTextStackColumns(doc, stack, detailColumnsAt(h));
    } else {
      this.drawTextStack(doc, stack, tx, detailTop, detailTextW, detailMaxY);
    }

    // Contact line — truncated to the details column so it can't run under
    // the divider into the notes area.
    if (contact) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...this.COLORS.black);
      doc.text(this.truncateToWidth(doc, contact, detailTextW), tx, contactY);
    }

    // Photos on the far right of the panel (body and/or eye photo).
    if (photos.length === 1) {
      // A single photo (usually just a body shot) gets a bigger, prominent
      // box. It's wider than the side-by-side size, and — since the centered
      // loft name/slogan rarely reach the far-right column — it rises toward
      // the panel top for extra height, dropping only if they actually
      // extend into its column. Panel height is unchanged, so the pedigree
      // grid below is unaffected.
      const photoW = Math.min(this.SUBJECT_PHOTO_MAX_WIDTH_SINGLE, w - detailsW - pad * 3);
      const photoLeft = x + w - pad - photoW;
      let top = y + pad + 1;
      if (loftName) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        const nameRight = x + (w + doc.getTextWidth(loftName.toUpperCase())) / 2;
        if (nameRight > photoLeft) top = Math.max(top, y + 10);
      }
      if (loftSlogan) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        const slgRight = x + (w + doc.getTextWidth(loftSlogan)) / 2;
        if (slgRight > photoLeft) top = Math.max(top, y + 15);
      }
      const photoH = (y + h - pad) - top;
      await this.drawPhoto(doc, photos[0], photoLeft, top, photoW, photoH);
    } else if (photos.length > 1) {
      const photoBoxH = h - (innerTop - y) - pad;
      const photoBoxW = Math.min(this.SUBJECT_PHOTO_MAX_WIDTH, photoBoxH + 8);
      let px = x + w - pad - photoBoxW;
      for (const photo of photos.slice().reverse()) {
        await this.drawPhoto(doc, photo, px, innerTop, photoBoxW, photoBoxH);
        px -= (photoBoxW + pad);
      }
    }

    // Continue overflow notes in the right-hand column, with the same widths
    // and height budget used above. Only ellipsize at the panel's height cap.
    if (notesText && notesWidth > 0) {
      doc.setTextColor(...this.COLORS.black);
      this.drawTextStackColumns(doc, notesStack, notesColumnsAt(h));
    }

    return h;
  }

  drawSubjectCard(doc, x, y, w, h, bird) {
    // Border
    doc.setDrawColor(...this.COLORS.black);
    doc.setLineWidth(0.5);
    doc.rect(x, y, w, h);

    // Left: identity lines
    const pad = 3;
    let tx = x + pad;
    let ty = y + pad + 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...this.COLORS.black);
    const idline = `(${bird.ring_number || ''})`;
    doc.text(idline, tx, ty);
    ty += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const color = bird.color ? `Color: ${bird.color}` : null;
    const sex = bird.sex ? `Sex: ${bird.sex === 'male' ? 'Cock' : bird.sex === 'female' ? 'Hen' : bird.sex}` : null;
    const breed = bird.breed ? `Strain: ${bird.breed}` : null;
    const parts = [color, sex, breed].filter(Boolean);
    if (parts.length) doc.text(parts.join(' | '), tx, ty);
  }

  async generatePedigree(bird, commonAncestors = [], settings = {}) {
    try {
      // Get full pedigree data
      const pedigreeData = await DatabaseService.getPedigree(bird.id, 5);

      // Create PDF document in portrait orientation
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Draw content (background first — everything else sits on top of it)
      await this.drawBackground(doc, settings);
      const topPanelH = await this.drawTopPanel(doc, bird, settings);
      await this.drawGridLayout(doc, bird, pedigreeData, settings, commonAncestors, topPanelH);
      this.drawFooter(doc, settings);

      return doc;
    } catch (error) {
      console.error('Error generating traditional PDF:', error);
      throw error;
    }
  }

  drawHeader(doc, settings) {
    // Header border
    doc.setDrawColor(...this.COLORS.black);
    doc.setLineWidth(1);
    doc.rect(this.MARGIN, this.MARGIN, this.CONTENT_WIDTH, this.HEADER_HEIGHT);
    
    // Loft name (smaller, centered)
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.COLORS.black);
    const loftName = settings.loftName || 'YOUR LOFT NAME';
    doc.text(loftName.toUpperCase(), this.PAGE_WIDTH / 2, this.MARGIN + 8, { align: 'center' });
    
    // Tagline/slogan (smaller)
    if (settings.loftSlogan) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(settings.loftSlogan, this.PAGE_WIDTH / 2, this.MARGIN + 14, { align: 'center' });
    }
    
    // Contact information (compact)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.COLORS.darkGray);
    
    let contactText = '';
    if (settings.loftPhone) contactText += `Tel: ${settings.loftPhone}`;
    if (settings.loftEmail) {
      if (contactText) contactText += ' | ';
      contactText += `eMail: ${settings.loftEmail}`;
    }
    if (contactText) {
      doc.text(contactText, this.PAGE_WIDTH / 2, this.MARGIN + 20, { align: 'center' });
    }
  }

  // Grid layout to match screenshot: 4 columns with hierarchical rows
  async drawGridLayout(doc, bird, pedigreeData, settings, commonAncestors = [], topPanelH = 0) {
    const startY = this.MARGIN; // header removed; top panel already drawn
    const gutter = 1;

    // Grid starts below the combined top panel
    const gridTop = startY + topPanelH + gutter;
    const footerReserve = 15;
    const gridHeight = this.PAGE_HEIGHT - gridTop - footerReserve;

    // Columns for parents->grandparents->great-grandparents (28/24/24/24)
    const totalGutters = gutter * 3;
    const widthNoGutters = this.CONTENT_WIDTH - totalGutters;
    const colW = [
      Math.round(widthNoGutters * 0.28),
      Math.round(widthNoGutters * 0.24),
      Math.round(widthNoGutters * 0.24),
      widthNoGutters - Math.round(widthNoGutters * 0.28) - Math.round(widthNoGutters * 0.24) - Math.round(widthNoGutters * 0.24)
    ];
    const colX = [
      this.MARGIN,
      this.MARGIN + colW[0] + gutter,
      this.MARGIN + colW[0] + gutter + colW[1] + gutter,
      this.MARGIN + colW[0] + gutter + colW[1] + gutter + colW[2] + gutter
    ];

    // Row heights: 2,4,8 blocks
    const h2 = gridHeight / 2;
    const h4 = gridHeight / 4;
    const h8 = gridHeight / 8;

    const sire = pedigreeData?.bird?.sire || null;
    const dam = pedigreeData?.bird?.dam || null;

    // Col 1: Parents — each under a SIRE/DAM heading, with the parent's
    // photo(s) shown when uploaded (traditional printed-pedigree style)
    await this.drawParentSection(doc, colX[0], gridTop, colW[0], h2, sire, 'SIRE', commonAncestors, settings);
    await this.drawParentSection(doc, colX[0], gridTop + h2, colW[0], h2, dam, 'DAM', commonAncestors, settings);

    // Col 2: Grandparents
    const pgf = sire?.sire || null; // paternal grandfather
    const pgm = sire?.dam || null;  // paternal grandmother
    const mgf = dam?.sire || null;  // maternal grandfather
    const mgm = dam?.dam || null;   // maternal grandmother
    this.drawPedigreeBox(doc, colX[1], gridTop, colW[1], h4, pgf, null, commonAncestors, false, 2, settings);
    this.drawPedigreeBox(doc, colX[1], gridTop + h4, colW[1], h4, pgm, null, commonAncestors, false, 2, settings);
    this.drawPedigreeBox(doc, colX[1], gridTop + h4 * 2, colW[1], h4, mgf, null, commonAncestors, false, 2, settings);
    this.drawPedigreeBox(doc, colX[1], gridTop + h4 * 3, colW[1], h4, mgm, null, commonAncestors, false, 2, settings);

    // Col 3: Great-grandparents
    const gg = [
      pgf?.sire || null, pgf?.dam || null,
      pgm?.sire || null, pgm?.dam || null,
      mgf?.sire || null, mgf?.dam || null,
      mgm?.sire || null, mgm?.dam || null
    ];
    for (let i = 0; i < 8; i++) {
      const y = gridTop + h8 * i;
      this.drawPedigreeBox(doc, colX[2], y, colW[2], h8, gg[i] || null, null, commonAncestors, false, 3, settings);
    }

    // Col 4: Optionally extend to 5th generation if available (draw as 'No data' if missing)
    const ggg = gg.flatMap(b => [b?.sire || null, b?.dam || null]); // 16 entries
    const h16 = gridHeight / 16;
    for (let i = 0; i < 16; i++) {
      const y = gridTop + h16 * i;
      this.drawPedigreeBox(doc, colX[3], y, colW[3], h16, ggg[i] || null, null, commonAncestors, true, 4, settings);
    }
  }

  // Column-1 parent slot: a "SIRE"/"DAM" heading, the parent's body photo when
  // uploaded, then the standard details box filling the rest of the slot.
  async drawParentSection(doc, x, y, w, h, bird, label, commonAncestors = [], settings = {}) {
    const labelH = 8;

    // Section heading — underlined serif italic, like classic printed pedigrees
    doc.setFont('times', 'bolditalic');
    doc.setFontSize(14);
    doc.setTextColor(...this.COLORS.black);
    doc.text(label, x + 0.5, y + 6);
    const labelW = doc.getTextWidth(label);
    doc.setDrawColor(...this.COLORS.black);
    doc.setLineWidth(0.4);
    doc.line(x + 0.5, y + 7.2, x + 0.5 + labelW, y + 7.2);

    // Photo: the parent's body photo only (the eye photo is not shown here),
    // centered, only when present.
    const bodyPhoto = bird ? bird.body_photo : null;
    let photoAreaH = 0;
    if (bodyPhoto) {
      const photoH = 30;
      const pad = 1;
      const py = y + labelH;
      const boxW = Math.min(w - pad * 2, 38);
      await this.drawPhoto(doc, bodyPhoto, x + (w - boxW) / 2, py, boxW, photoH);
      photoAreaH = photoH + 1;
    }

    // Details box fills the remaining slot height (gray 'no data' when empty)
    this.drawPedigreeBox(doc, x, y + labelH + photoAreaH, w, h - labelH - photoAreaH, bird, null, commonAncestors, false, 1, settings);
  }

  // Draw a single pedigree box (or 'No data') with colored header strip
  drawPedigreeBox(doc, x, y, w, h, bird, headerColorRGB = null, commonAncestors = [], isLastGeneration = false, generation = 1, settings = {}) {
    // Header strip
    const headerH = 6;
    if (bird) {
      // Draw the border first (behind everything)
      doc.setDrawColor(...this.COLORS.black);
      doc.setLineWidth(0.4);
      doc.rect(x, y, w, h);
      // Normalize IDs to numbers for comparison
      const birdId = typeof bird.id === 'string' ? parseInt(bird.id, 10) : bird.id;
      const normalizedAncestors = commonAncestors.map(id => typeof id === 'string' ? parseInt(id, 10) : id);
      const isCommonAncestor = birdId && normalizedAncestors.includes(birdId);
      
      // Get the highlight color from the duplicateColors map if available
      let headerColor = headerColorRGB || [235, 235, 235];
      
      if (isCommonAncestor) {
        // Check if we have a specific color for this duplicate group
        const duplicateInfo = settings.duplicateColors?.get(birdId);
        if (duplicateInfo) {
          // Extract RGB values from the color string (e.g., 'rgb(255,200,200)')
          const rgbMatch = duplicateInfo.color.match(/\d+/g);
          if (rgbMatch && rgbMatch.length >= 3) {
            headerColor = [
              parseInt(rgbMatch[0]),
              parseInt(rgbMatch[1]),
              parseInt(rgbMatch[2])
            ];
          } else {
            // Fallback to light green if color parsing fails
            headerColor = [144, 238, 144];
          }
        } else {
          // Fallback to light green if no specific color is set
          headerColor = [144, 238, 144];
        }
      }
      
      // Apply the header color (after border)
      doc.setFillColor(...headerColor);
      doc.rect(x, y, w, headerH, 'F');
      
      // Redraw the border on top of the filled area
      doc.setDrawColor(...this.COLORS.black);
      doc.setLineWidth(0.4);
      doc.rect(x, y, w, headerH);

      // Header text: Ring (left) and Year (right)
      let ring = bird.ring_number || '';
      let year = null;
      if (bird.date_of_birth) {
        const d = new Date(bird.date_of_birth);
        if (!isNaN(d.getTime())) year = d.getFullYear();
      }
      if (year == null && typeof bird.year !== 'undefined') year = bird.year;
      
      doc.setTextColor(...this.COLORS.black);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8); // Slightly smaller font for better fit
      
      // Format ring number to ensure space between letters and numbers
      if (ring) {
        // Add space between letters and numbers if not already present
        const match = ring.match(/^([A-Za-z]+)([0-9].*)$/);
        if (match && !ring.includes(' ')) {
          ring = `${match[1]} ${match[2]}`;
        }
        
        // Draw ring number (left-aligned)
        doc.text(ring, x + 2, y + headerH - 1.5);
      }
      
      // Draw year (right-aligned)
      if (year != null) {
        const shortYear = String(year).slice(-2);
        doc.text(shortYear, x + w - 4, y + headerH - 1.5, { align: 'right' });
      }
    } else {
      doc.setFillColor(...this.COLORS.lightGray);
      doc.rect(x, y, w, h, 'F');
      return;
    }

    // Text padding - keeping horizontal padding reduced
    const pad = 2; // Horizontal padding (reduced from 4 to 2)
    // 3.5 (not 4) so that two 4mm lines (name + strain) fit inside the
    // smallest 4th-generation boxes (~14.9mm tall: baselines y+9.5 and
    // y+13.5 vs bottomY y+13.9) — at 4 the second line missed by 0.1mm and
    // every gen-4 box collapsed to a single ellipsized line.
    const vpad = 3.5;
    const tx = x + pad;
    const ty = y + headerH + vpad;
    const textWidth = w - (pad * 2);
    const bottomY = y + h - 1; // lowest baseline allowed inside the box

    doc.setTextColor(...this.COLORS.black);

    // Each field wraps within the box and flows below the previous one, so
    // a long strain pushes the notes down instead of being drawn underneath
    // them. The stack clamps at the box bottom: the last visible line gets
    // "..." when anything (even a whole field) is cut off, and text never
    // bleeds into the box below.
    const detailSize = bird.name ? 7 : 8;
    const stack = [];
    if (bird.name) stack.push({ text: bird.name, fontStyle: 'bold', size: 8, lineH: 4 });
    if (bird.breed) stack.push({ text: `Strain: ${bird.breed}`, fontStyle: 'normal', size: detailSize, lineH: 4 });
    if (generation <= 2 && bird.color) stack.push({ text: `Color: ${bird.color}`, fontStyle: 'normal', size: detailSize, lineH: 4 });
    if (bird.notes && bird.notes.trim()) stack.push({ text: bird.notes, fontStyle: 'normal', size: 7, lineH: 3 });
    this.drawTextStack(doc, stack, tx, ty, textWidth, bottomY);
  }

  drawNotesPanel(doc, x, y, w, h, text) {
    // Border
    doc.setDrawColor(...this.COLORS.black);
    doc.setLineWidth(0.4);
    doc.rect(x, y, w, h);

    // Title
    const pad = 3;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...this.COLORS.black);
    doc.text('Notes:', x + pad, y + pad + 2);

    // Content
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...this.COLORS.darkGray);
    const lines = doc.splitTextToSize(text, w - pad * 2);
    doc.text(lines, x + pad, y + pad + 8);
  }

  drawEmptyBirdBox(doc, x, y, label) {
    // Draw box border
    doc.setDrawColor(...this.COLORS.lightGray);
    doc.setLineWidth(0.5);
    doc.rect(x, y, this.BIRD_BOX_WIDTH, this.BIRD_BOX_HEIGHT);
    
    // Photo area (left side)
    const photoX = x + 2;
    const photoY = y + 2;
    doc.setFillColor(...this.COLORS.lightGray);
    doc.rect(photoX, photoY, this.PHOTO_SIZE, this.PHOTO_SIZE, 'F');
    
    // Empty bird label
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...this.COLORS.darkGray);
    const infoX = x + this.PHOTO_SIZE + 5;
    const infoY = y + this.BIRD_BOX_HEIGHT / 2 + 2;
    doc.text(label, infoX, infoY);
  }

  drawFooter(doc, settings) {
    const footerY = this.PAGE_HEIGHT - 10;
    
    // Footer line
    doc.setDrawColor(...this.COLORS.lightGray);
    doc.setLineWidth(0.5);
    doc.line(this.MARGIN, footerY - 5, this.PAGE_WIDTH - this.MARGIN, footerY - 5);
    
    // Left side - Copyright
    doc.setFontSize(7);
    doc.setTextColor(...this.COLORS.black);
    doc.setFont('helvetica', 'normal');
    
    const copyright = settings.breederName ?
      `Produced by ${settings.breederName}` :
      'Produced by Pigeon Pedigree Manager';

    doc.text(copyright, this.MARGIN, footerY);

    // Right side - Loft name
    if (settings.loftName) {
      doc.text(`For: ${settings.loftName}`, this.PAGE_WIDTH - this.MARGIN, footerY, { align: 'right' });
    }

    // Center - Loft website (only when the breeder has configured one)
    if (settings.loftWebsite) {
      doc.text(settings.loftWebsite, this.PAGE_WIDTH / 2, footerY, { align: 'center' });
    }

    // Product credit line — every shared pedigree quietly advertises the app.
    // Mid-grey reads as a soft credit on white, but it is the one string on
    // the page that a washed background could swallow (the wash floor only
    // guarantees contrast for BLACK text), so darken it when one is set.
    doc.setFontSize(6);
    doc.setTextColor(...(settings.pedigreeBackground ? this.COLORS.darkGray : this.COLORS.gray));
    doc.text(
      'Made with Pigeon Pedigree Manager  ·  www.pigeonpedigreemanager.com',
      this.PAGE_WIDTH / 2,
      footerY + 4,
      { align: 'center' }
    );
  }

  // Filename base for a saved pedigree: "<ring>-<2-digit birth year>", e.g.
  // "GB-1992-26", prefixed with the bird's name when it has one, e.g.
  // "Blue-Boy-GB-1992-26". Falls back to whatever parts exist.
  pedigreeFileBaseName(bird) {
    let year = null;
    if (bird.date_of_birth) {
      const d = new Date(bird.date_of_birth);
      if (!isNaN(d.getTime())) year = d.getFullYear();
    }
    if (year == null && bird.year != null && bird.year !== '') year = bird.year;
    const shortYear = year != null ? String(year).slice(-2) : '';

    const parts = [
      bird.name ? slugify(bird.name) : '',
      slugify(bird.ring_number),
      shortYear
    ].filter(Boolean);

    return parts.join('-') || 'Pedigree';
  }

  async exportToFile(bird, commonAncestors = [], settings = {}, options = {}) {
    try {
      const doc = await this.generatePedigree(bird, commonAncestors, settings);
      const shouldDownload = options.download !== false;

      const filename = `${this.pedigreeFileBaseName(bird)}.pdf`;

      // Use Electron's native save dialog if available, otherwise fall back to browser download
      if (shouldDownload && window.electronAPI && window.electronAPI.saveFile) {
        const arrayBuffer = doc.output('arraybuffer');
        const result = await window.electronAPI.saveFile({
          defaultFilename: filename,
          data: new Uint8Array(arrayBuffer)
        });
        if (result.canceled) {
          return { success: false, canceled: true };
        }
        if (!result.success) {
          throw new Error(result.error || 'Failed to save PDF');
        }
        return { success: true, filePath: result.filePath };
      } else {
        // Browser export. Serialize ONCE (doc.save would re-build the whole
        // document a second time); the same blob backs the download, the
        // "View now" URL, and the mobile share sheet. The CALLER owns
        // viewUrl and must URL.revokeObjectURL it when done — see
        // PedigreeView.
        const blob = doc.output('blob');
        const file = new File([blob], filename, { type: 'application/pdf' });
        // Touch devices that can share files get a "Save / View" share-sheet
        // button in the success popup (phones handle PDFs through their
        // share targets far better than through a bare blob tab).
        const canShare = Boolean(
          typeof navigator.canShare === 'function' &&
          navigator.canShare({ files: [file] }) &&
          window.matchMedia('(pointer: coarse)').matches
        );
        // The automatic download is skipped ONLY on iOS, where Safari
        // ignores the anchor `download` attribute for blob URLs and would
        // navigate the tab to the raw PDF instead of saving it — there the
        // share sheet (Save to Files) IS the save mechanism. Android's
        // downloads work like desktop's (Downloads folder + notification),
        // so Android keeps the download AND gets the share button.
        // (iPadOS masquerades as MacIntel but exposes multi-touch.)
        const isIOS =
          /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const downloaded = shouldDownload && !(canShare && isIOS);
        const viewUrl = URL.createObjectURL(blob);
        if (downloaded) {
          const link = document.createElement('a');
          link.href = viewUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        return { success: true, viewUrl, file, canShare, downloaded, filename };
      }

    } catch (error) {
      console.error('Error exporting traditional PDF:', error);
      throw error;
    }
  }
}

export default TraditionalPdfGenerator;
